import React, { useState } from 'react'
import makeStyles from '@mui/styles/makeStyles'
import Switch from '@mui/material/Switch'
import FormControlLabel from '@mui/material/FormControlLabel'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import { useSelector } from '../../redux/hooks/useSelector'
import { OrderAction } from '../../redux/reducers/orders'
import { ACTION_BROKER_SEND_TO_FILL, ACTION_BROKER_SEND_TO_PARTIAL_FILL } from '../../constants/codeLists'
import NumberFormat from 'react-number-format'
import { useTranslation } from 'react-i18next'
import useMobileWidth from '../../hooks/useMobileWidth'
import { tokens } from '../../styles/tokens'
import classNames from 'classnames'

const useStyles = makeStyles(theme => ({
  table: {
    minWidth: '40vw',
    [theme.breakpoints.down('sm')]: {
      minWidth: 'auto',
    },
  },
  fillTableContainer: {
    minHeight: '60vh',
  },
  fillCell: {},
  actionCell: {
    fontStyle: 'italic',
  },
  filterSwitch: {
    marginLeft: '5px',
    marginTop: '5px',
  },
  // Karty pro mobil - nahrazuji sirokou tabulku (9 sloupcu), aby se nemuselo nic posouvat do stran.
  mobileList: {
    display: 'flex',
    flexDirection: 'column',
  },
  mobileCard: {
    padding: theme.spacing(1.5, 0),
    borderBottom: `1px solid ${tokens.color.neutral[200]}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  mobileCardGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: theme.spacing(0.75, 1.5),
  },
  mobileCardField: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  mobileCardLabel: {
    margin: 0,
    fontSize: '0.65rem',
    color: tokens.color.neutral[500],
  },
  mobileCardValue: {
    margin: '2px 0 0',
    fontSize: '0.75rem',
    fontWeight: 600,
    overflowWrap: 'break-word',
  },
}))

export const FillTable = () => {
  const [showAll, setShowAll] = useState(true)
  const classes = useStyles()
  const order = useSelector(state => state.orders.order)
  const { t } = useTranslation()
  const isMobile = useMobileWidth()

  const isFill = (code: string | null) =>
    code === ACTION_BROKER_SEND_TO_PARTIAL_FILL || code === ACTION_BROKER_SEND_TO_FILL

  return (
    <>
      <TableContainer component={Paper} className={classes.fillTableContainer}>
        {isMobile ? (
          <div className={classes.mobileList}>
            {order.actions
              .filter((e: OrderAction) => showAll || isFill(e.code))
              .map((row: OrderAction, i: Number) => (
                <div className={classes.mobileCard} key={(row.extenalId || 'ID') + '-' + i}>
                  <div className={classes.mobileCardGrid}>
                    <div className={classes.mobileCardField}>
                      <p className={classes.mobileCardLabel}>{t('FILL')}</p>
                      <p className={classes.mobileCardValue}>{row.extenalId}</p>
                    </div>
                    <div className={classes.mobileCardField}>
                      <p className={classes.mobileCardLabel}>{t('DATE')}</p>
                      <p className={classes.mobileCardValue}>{row.date}</p>
                    </div>
                    <div className={classes.mobileCardField}>
                      <p className={classes.mobileCardLabel}>{t('TYPE')}</p>
                      <p className={isFill(row.code) ? classes.mobileCardValue : classNames(classes.mobileCardValue, classes.actionCell)}>
                        {row.code ? t(row.code) : ''}
                      </p>
                    </div>
                    <div className={classes.mobileCardField}>
                      <p className={classes.mobileCardLabel}>{t('QUANTITY')}</p>
                      <p className={classes.mobileCardValue}>{row.quantity}</p>
                    </div>
                    <div className={classes.mobileCardField}>
                      <p className={classes.mobileCardLabel}>{t('PRICE')}</p>
                      <p className={classes.mobileCardValue}>
                        {row.price && (
                          <NumberFormat
                            value={row.price}
                            displayType="text"
                            thousandSeparator=" "
                            fixedDecimalScale
                            decimalScale={2}
                            decimalSeparator=","
                          />
                        )}
                      </p>
                    </div>
                    <div className={classes.mobileCardField}>
                      <p className={classes.mobileCardLabel}>{t('OMT_MARKET')}</p>
                      <p className={classes.mobileCardValue}>{row.market}</p>
                    </div>
                    <div className={classes.mobileCardField}>
                      <p className={classes.mobileCardLabel}>{t('CURRENCY')}</p>
                      <p className={classes.mobileCardValue}>{row.currency}</p>
                    </div>
                    <div className={classes.mobileCardField}>
                      <p className={classes.mobileCardLabel}>{t('FEE')}</p>
                      <p className={classes.mobileCardValue}>
                        {row.fee && (
                          <NumberFormat
                            value={row.fee}
                            displayType="text"
                            thousandSeparator=" "
                            fixedDecimalScale
                            decimalScale={2}
                            decimalSeparator=","
                          />
                        )}
                      </p>
                    </div>
                    <div className={classes.mobileCardField}>
                      <p className={classes.mobileCardLabel}>{t('AMOUNT')}</p>
                      <p className={classes.mobileCardValue}>
                        {row.amount && (
                          <NumberFormat
                            value={row.amount}
                            displayType="text"
                            thousandSeparator=" "
                            fixedDecimalScale
                            decimalScale={2}
                            decimalSeparator=","
                          />
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        ) : (
        <Table size="small" stickyHeader className={classes.table} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell>{t('FILL')}</TableCell>
              <TableCell align="right">{t('DATE')}</TableCell>
              <TableCell align="right">{t('TYPE')}</TableCell>
              <TableCell align="right">{t('QUANTITY')}</TableCell>
              <TableCell align="right">{t('PRICE')}</TableCell>
              <TableCell align="right">{t('OMT_MARKET')}</TableCell>
              <TableCell align="right">{t('CURRENCY')}</TableCell>
              <TableCell align="right">{t('FEE')}</TableCell>
              <TableCell align="right">{t('AMOUNT')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {order.actions
              .filter((e: OrderAction) => showAll || isFill(e.code))
              .map((row: OrderAction, i: Number) => (
                <TableRow key={(row.extenalId || 'ID') + '-' + i}>
                  <TableCell component="th" scope="row">
                    {row.extenalId}
                  </TableCell>
                  <TableCell align="right">{row.date}</TableCell>
                  <TableCell align="right" className={isFill(row.code) ? classes.fillCell : classes.actionCell}>
                    {row.code ? t(row.code) : ''}
                  </TableCell>
                  <TableCell align="right">{row.quantity}</TableCell>
                  <TableCell align="right">
                    {row.price && (
                      <NumberFormat
                        value={row.price}
                        displayType="text"
                        thousandSeparator=" "
                        fixedDecimalScale
                        decimalScale={2}
                        decimalSeparator=","
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">{row.market}</TableCell>
                  <TableCell align="right">{row.currency}</TableCell>
                  <TableCell align="right">
                    {row.fee && (
                      <NumberFormat
                        value={row.fee}
                        displayType="text"
                        thousandSeparator=" "
                        fixedDecimalScale
                        decimalScale={2}
                        decimalSeparator=","
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {row.amount && (
                      <NumberFormat
                        value={row.amount}
                        displayType="text"
                        thousandSeparator=" "
                        fixedDecimalScale
                        decimalScale={2}
                        decimalSeparator=","
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        )}
      </TableContainer>

      <FormControlLabel
        className={classes.filterSwitch}
        control={
          <Switch
            checked={showAll}
            onChange={(event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => setShowAll(checked)}
            name="showAll"
            color="primary"
            size="small"
          />
        }
        label={t('FILL_INCLUDE')}
      />
    </>
  )
}
